import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import SEO from "../../components/SEO";
import { MEDIA } from "../../lib/constants";

const POSTS = [
  {
    slug: "visiting-district-wellness-center-dc",
    category: "Visitor Guide",
    title: "Visiting DWC: What to Expect at 4915 Georgia Ave NW",
    excerpt:
      "A first-timer's walkthrough of District Wellness Center — parking, ID rules, the lounge, and how to get in and out in 10 minutes.",
    readMinutes: 5,
    image: MEDIA.storefrontDoor,
    featured: true,
  },
  {
    slug: "khalifa-kush-dc-dispensary",
    category: "Strain Spotlight",
    title: "Khalifa Kush in DC: Wiz Khalifa's Legendary Strain at DWC",
    excerpt:
      "Effects, terpene profile, flower jar vs. pre-roll — everything DC medical patients want to know about Khalifa Kush at District Wellness Center.",
    readMinutes: 5,
    image: MEDIA.khalifaKush,
  },
  {
    slug: "alt-sol-cannabis-dc",
    category: "Brand Spotlight",
    title: "Alt Sol Cannabis in DC: Duct Tape, Blue Sharpies, Hashburger",
    excerpt:
      "Meet one of DC's most trusted small-batch flower brands — hand-finished jars, honest labels, and drops that land on the DWC menu.",
    readMinutes: 5,
    image: MEDIA.altSolJars,
  },
  {
    slug: "how-to-buy-medical-cannabis-dc",
    category: "Patient Guide",
    title: "How to Buy Medical Cannabis in Washington, DC",
    excerpt:
      "A simple guide for DC residents and out-of-state visitors — self-certification, reciprocity, temporary registrations and what to bring.",
    readMinutes: 6,
    image: MEDIA.wallGallery,
  },
  {
    slug: "how-to-get-medical-marijuana-card-dc",
    category: "Registration",
    title: "How to Get a Medical Marijuana Card in DC (2026)",
    excerpt:
      "Step-by-step guide to getting a DC medical card. Self-certify online in minutes — no doctor's visit required.",
    readMinutes: 4,
    image: MEDIA.lounge,
  },
  {
    slug: "out-of-state-dispensary-dc",
    category: "Visitor Guide",
    title: "Out-of-State Patient Guide to DC Dispensaries",
    excerpt:
      "Visiting Washington, DC? Everything non-residents need to know about buying medical cannabis legally in the District.",
    readMinutes: 5,
    image: MEDIA.storefront,
  },
  {
    slug: "best-cannabis-dispensary",
    category: "About DWC",
    title: "What Makes the Best Cannabis Dispensary in DC",
    excerpt:
      "How to spot a great DC dispensary — sourcing, safety, staff knowledge, and why patients trust District Wellness Center.",
    readMinutes: 5,
    image: MEDIA.salesFloor2,
  },
];

export default function BlogIndex() {
  const [featured, ...rest] = POSTS;
  return (
    <>
      <SEO
        title="DWC Blog · Medical Cannabis Guides for Washington, DC | District Wellness Center"
        description="Articles, guides and education from District Wellness Center — DC medical cannabis registration, self-certification, out-of-state rules, product education and more."
        path="/blog"
        breadcrumbs={[{ name: "Blog", path: "/blog" }]}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-brand mb-5">
            <BookOpen size={14} />
            The DWC Blog
          </div>
          <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            Guides & <em className="text-brand">education</em>.
          </h1>
          <p className="mt-6 text-ink-muted text-lg max-w-2xl leading-relaxed">
            Practical guides written for DC medical cannabis patients — from
            first-time self-certification to product deep-dives. Updated
            regularly.
          </p>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="blog-featured">
        <div className="max-w-7xl mx-auto px-6">
          <Link
            to={`/blog/${featured.slug}`}
            className="group grid lg:grid-cols-2 gap-10 items-center bg-white border border-black/10 hover:border-brand transition-colors overflow-hidden"
            data-testid="blog-featured-card"
          >
            <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden">
              <img
                src={featured.image}
                alt={featured.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              />
            </div>
            <div className="p-8 sm:p-12">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">
                Featured · {featured.category} · {featured.readMinutes} min read
              </div>
              <h2 className="font-serif text-3xl sm:text-5xl leading-[1.05] tracking-tight group-hover:text-brand transition-colors">
                {featured.title}
              </h2>
              <p className="mt-5 text-ink-muted text-base sm:text-lg leading-relaxed">
                {featured.excerpt}
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-brand border-b border-brand/40 pb-1 group-hover:border-brand transition-colors">
                Read the guide <ArrowUpRight size={14} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="border-b border-black/10 pb-4 mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">
              More reading
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {rest.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group flex flex-col bg-white border border-black/10 hover:border-brand transition-colors overflow-hidden"
                data-testid={`blog-card-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">
                    {p.category} · {p.readMinutes} min
                  </div>
                  <h3 className="font-serif text-xl leading-tight group-hover:text-brand transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                    {p.excerpt}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-brand">
                    Read more <ArrowUpRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
